import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { puzzleApi } from '../utils/api'
import { calculateNumberedCells } from '../utils/gridUtils'

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  backLink: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    fontWeight: '600',
    textDecoration: 'none',
  },
  printBtn: {
    padding: '8px 16px',
    border: '2px solid var(--color-border)',
    background: 'var(--color-primary)',
    color: '#fff',
    fontWeight: '600',
    cursor: 'pointer',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    marginBottom: '8px',
    textAlign: 'center',
  },
  meta: {
    textAlign: 'center',
    color: 'var(--color-muted)',
    marginBottom: '32px',
  },
  gridContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  grid: {
    display: 'inline-block',
    border: '2px solid #000',
  },
  row: {
    display: 'flex',
  },
  cell: {
    width: '28px',
    height: '28px',
    border: '1px solid #000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    background: '#fff',
  },
  cellBlack: {
    background: '#000',
  },
  cellNumber: {
    position: 'absolute',
    top: '1px',
    left: '2px',
    fontSize: '8px',
    fontWeight: '700',
    lineHeight: 1,
  },
  cluesContainer: {
    display: 'flex',
    gap: '48px',
    flexWrap: 'wrap',
  },
  cluesColumn: {
    flex: '1',
    minWidth: '250px',
  },
  cluesTitle: {
    fontWeight: '700',
    marginBottom: '12px',
    paddingBottom: '8px',
    borderBottom: '2px solid var(--color-border)',
  },
  clueItem: {
    marginBottom: '8px',
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },
  clueNum: {
    fontWeight: '700',
    marginRight: '6px',
  },
  loading: {
    textAlign: 'center',
    padding: '48px',
    color: 'var(--color-muted)',
  },
}

export default function PrintView() {
  const { id } = useParams()
  const [puzzle, setPuzzle] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchPuzzle() {
      try {
        const data = await puzzleApi.get(id)
        setPuzzle(data)
      } catch (err) {
        console.error('Failed to fetch puzzle:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchPuzzle()
  }, [id])

  const numberedCells = useMemo(() => {
    if (!puzzle?.grid) return { cells: {}, acrossEntries: [], downEntries: [] }
    return calculateNumberedCells(puzzle.grid)
  }, [puzzle?.grid])

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return <div style={styles.loading}>Loading puzzle...</div>
  }

  if (!puzzle) {
    return (
      <div style={styles.loading}>
        <p>Puzzle not found</p>
        <Link to="/" style={{ color: 'var(--color-accent)' }}>Back to Home</Link>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.header} className="no-print">
        <Link to={`/puzzle/${id}`} style={styles.backLink}>
          ← Back to Puzzle
        </Link>
        <button style={styles.printBtn} onClick={handlePrint}>
          Print
        </button>
      </div>

      <h1 style={styles.title}>{puzzle.theme || 'Crossword Puzzle'}</h1>
      {puzzle.creatorName && (
        <p style={styles.meta}>By {puzzle.creatorName}</p>
      )}

      <div style={styles.gridContainer}>
        <div style={styles.grid}>
          {puzzle.grid.map((row, rowIdx) => (
            <div key={rowIdx} style={styles.row}>
              {row.map((cell, colIdx) => {
                const cellNum = numberedCells.cells[`${rowIdx}-${colIdx}`]
                return (
                  <div
                    key={colIdx}
                    style={{
                      ...styles.cell,
                      ...(cell.isBlack ? styles.cellBlack : {}),
                    }}
                  >
                    {cellNum && !cell.isBlack && (
                      <span style={styles.cellNumber}>{cellNum}</span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      <div style={styles.cluesContainer}>
        <div style={styles.cluesColumn}>
          <h3 style={styles.cluesTitle}>Across</h3>
          {numberedCells.acrossEntries.map(entry => (
            <div key={entry.num} style={styles.clueItem}>
              <span style={styles.clueNum}>{entry.num}.</span>
              {puzzle.clues?.across?.[entry.num] || '—'}
            </div>
          ))}
        </div>

        <div style={styles.cluesColumn}>
          <h3 style={styles.cluesTitle}>Down</h3>
          {numberedCells.downEntries.map(entry => (
            <div key={entry.num} style={styles.clueItem}>
              <span style={styles.clueNum}>{entry.num}.</span>
              {puzzle.clues?.down?.[entry.num] || '—'}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
